// src/draw/gazeTelemetry.ts
//
// Live gaze-telemetry accumulator for Draw Mode. main.ts's Watch loop feeds the
// primary tracked face in here once per frame while the drawing ritual runs;
// DrawMode resets it at ritual start and snapshots it at report time so fusion.ts
// can fuse "how the webcam saw you" with "how you drew".
//
// Pure logic, NO DOM. This is behavioural NOVELTY observation, not a clinical or
// diagnostic measurement. No em-dashes anywhere (repo convention). Strict TS,
// 2-space indent.

import type { GazeTelemetry as GazeTelemetryData, GazeTelemetrySource } from "./types";
import type { TrackedFace } from "../types";
import { CONFIG } from "../config";

/** Discrete eyelid state derived from eyeOpen via the lockOn EAR thresholds. */
type EyeState = "open" | "closed" | "unknown";

/**
 * Stillness scaling. centroidVariance is the summed positional variance of the
 * normalized head centroid (varX + varY); its square root is the combined
 * motion magnitude in normalized-image units. Typical micro-motion sits around
 * 0.02 (k * 0.02 = 0.12, stillness near 0.88) while large head sway sits around
 * 0.15 (k * 0.15 = 0.9, stillness near 0.1). k = 6 gives that mapping.
 */
const STILLNESS_K = 6;

/** Upper clamp for the reported blinks-per-minute (defends against short rituals). */
const MAX_BLINK_RATE = 120;

/** How many facial-action names snapshot() reports, most frequent first. */
const DOMINANT_ACTIONS_COUNT = 3;

function clamp01(v: number): number {
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}

export class GazeTelemetry implements GazeTelemetrySource {
  private samples = 0;
  private firstT = 0;
  private lastT = 0;
  private haveFirst = false;

  private sumHeadFacing = 0;
  private sumGazeCentered = 0;
  private sumDistance = 0;
  private minDistance = 0;
  private maxDistance = 0;
  private haveDistance = false;

  private closedSamples = 0;

  // Eyelid state machine (blink + peek detection).
  private eyeState: EyeState = "unknown";
  private blinkArmed = false;
  private blinks = 0;
  private peekCount = 0;

  // Running mean + variance of the head centroid (Welford's algorithm, one pass
  // per axis), used for the stillness measure.
  private cxMean = 0;
  private cyMean = 0;
  private cxM2 = 0;
  private cyM2 = 0;

  // Tally of facial-action names across the ritual.
  private actionTally = new Map<string, number>();

  /** Clear every accumulator for a fresh ritual. */
  reset(): void {
    this.samples = 0;
    this.firstT = 0;
    this.lastT = 0;
    this.haveFirst = false;

    this.sumHeadFacing = 0;
    this.sumGazeCentered = 0;
    this.sumDistance = 0;
    this.minDistance = 0;
    this.maxDistance = 0;
    this.haveDistance = false;

    this.closedSamples = 0;

    this.eyeState = "unknown";
    this.blinkArmed = false;
    this.blinks = 0;
    this.peekCount = 0;

    this.cxMean = 0;
    this.cyMean = 0;
    this.cxM2 = 0;
    this.cyM2 = 0;

    this.actionTally = new Map<string, number>();
  }

  /** Fold one frame's primary-face observation in. No-op when face is null. */
  sample(face: TrackedFace | null, nowMs: number): void {
    if (face === null) return;

    const { earOpen, earClosed } = CONFIG.lockOn;

    // Timestamps (presence span).
    if (!this.haveFirst) {
      this.firstT = nowMs;
      this.haveFirst = true;
    }
    this.lastT = nowMs;

    this.samples += 1;

    // Running sums for the means.
    this.sumHeadFacing += face.headFacing;
    this.sumGazeCentered += face.gazeCentered;

    // Distance sum + drift bounds.
    const d = face.distanceM;
    this.sumDistance += d;
    if (!this.haveDistance) {
      this.minDistance = d;
      this.maxDistance = d;
      this.haveDistance = true;
    } else {
      if (d < this.minDistance) this.minDistance = d;
      if (d > this.maxDistance) this.maxDistance = d;
    }

    // Eyes-closed fraction.
    if (face.eyeOpen <= earClosed) {
      this.closedSamples += 1;
    }

    // Eyelid state machine. The mid-zone (between earClosed and earOpen) holds
    // the previous state so hysteresis stops boundary chatter. A blink is one
    // full open->closed->open cycle; a peek is any closed->open transition
    // (eyes were shut during the ritual, then opened).
    let next: EyeState = this.eyeState;
    if (face.eyeOpen >= earOpen) {
      next = "open";
    } else if (face.eyeOpen <= earClosed) {
      next = "closed";
    }

    if (next !== this.eyeState) {
      if (next === "closed") {
        // Only a close that followed an open arms a complete blink cycle.
        if (this.eyeState === "open") this.blinkArmed = true;
      } else if (next === "open") {
        if (this.eyeState === "closed") {
          this.peekCount += 1;
          if (this.blinkArmed) {
            this.blinks += 1;
            this.blinkArmed = false;
          }
        }
      }
      this.eyeState = next;
    }

    // Head-centroid running mean + variance (Welford, per axis).
    const nx = this.samples;
    const dxc = face.cx - this.cxMean;
    this.cxMean += dxc / nx;
    this.cxM2 += dxc * (face.cx - this.cxMean);

    const dyc = face.cy - this.cyMean;
    this.cyMean += dyc / nx;
    this.cyM2 += dyc * (face.cy - this.cyMean);

    // Facial-action tally.
    for (const action of face.actions) {
      const prev = this.actionTally.get(action.name) ?? 0;
      this.actionTally.set(action.name, prev + 1);
    }
  }

  /** Freeze the accumulated behaviour into an immutable GazeTelemetry. */
  snapshot(): GazeTelemetryData {
    const n = this.samples;
    const valid = n > 0;

    const presenceMs = this.haveFirst ? Math.max(0, this.lastT - this.firstT) : 0;

    const meanHeadFacing = n > 0 ? clamp01(this.sumHeadFacing / n) : 0;
    const meanGazeCentered = n > 0 ? clamp01(this.sumGazeCentered / n) : 0;
    const eyesClosedFraction = n > 0 ? clamp01(this.closedSamples / n) : 0;
    const meanDistanceM = n > 0 ? this.sumDistance / n : 0;
    const distanceDriftM = this.haveDistance ? Math.max(0, this.maxDistance - this.minDistance) : 0;

    // Blink rate per minute, clamped. Needs a non-trivial presence window so a
    // single blink in a few ms does not blow up to an absurd rate.
    let blinkRate = 0;
    if (presenceMs > 0) {
      blinkRate = this.blinks / (presenceMs / 60000);
      if (blinkRate > MAX_BLINK_RATE) blinkRate = MAX_BLINK_RATE;
      if (blinkRate < 0) blinkRate = 0;
    }

    // Stillness from combined centroid variance. Welford M2 / n is the
    // population variance of each axis.
    let stillness = 0;
    if (n > 0) {
      const varX = this.cxM2 / n;
      const varY = this.cyM2 / n;
      const motion = Math.sqrt(Math.max(0, varX + varY));
      stillness = clamp01(1 - STILLNESS_K * motion);
    }

    // Top action names by tally, most frequent first.
    const dominantActions = [...this.actionTally.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, DOMINANT_ACTIONS_COUNT)
      .map(([name]) => name);

    return {
      samples: n,
      presenceMs,
      meanHeadFacing,
      meanGazeCentered,
      eyesClosedFraction,
      blinkRate,
      peekCount: this.peekCount,
      stillness,
      meanDistanceM,
      distanceDriftM,
      dominantActions,
      valid,
    };
  }
}
